import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Slider,
  Typography,
  IconButton,
} from '@mui/material';
import { Close, RotateRight, RotateLeft } from '@mui/icons-material';
import Cropper from 'react-easy-crop';
import getCroppedImg from '../utils/cropImage';

export default function PhotoCropModal({ open, onClose, imageSrc, onCropComplete }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [loading, setLoading] = useState(false);

  const onCropChange = useCallback((crop) => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback((zoom) => {
    setZoom(zoom);
  }, []);

  const onCropCompleteCallback = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleRotateLeft = () => {
    setRotation((prev) => prev - 90);
  };

  const handleRotateRight = () => {
    setRotation((prev) => prev + 90);
  };

  const handleCrop = useCallback(async () => {
    if (!croppedAreaPixels) return;

    setLoading(true);
    try {
      const croppedImage = await getCroppedImg(
        imageSrc,
        croppedAreaPixels,
        rotation
      );

      onCropComplete(croppedImage);
      onClose();
    } catch (error) {
      console.error('Error cropping image:', error);
    } finally {
      setLoading(false);
    }
  }, [croppedAreaPixels, imageSrc, rotation, onCropComplete, onClose]);

  const handleClose = () => {
    // Reset state when closing
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh', maxHeight: '600px' }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Typography variant="h6">Crop Profile Photo</Typography>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, flex: 1 }}>
        <Box sx={{ position: 'relative', height: '300px', width: '100%' }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1} // Square aspect ratio for profile photos
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onCropComplete={onCropCompleteCallback}
            rotation={rotation}
            cropShape="round"
            showGrid={false}
          />
        </Box>

        <Box sx={{ p: 3, pt: 2 }}>
          {/* Zoom Control */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Zoom: {Math.round(zoom * 100)}%
            </Typography>
            <Slider
              value={zoom}
              min={0.5}
              max={3}
              step={0.1}
              onChange={(e, value) => setZoom(value)}
              aria-labelledby="zoom-slider"
            />
          </Box>

          {/* Rotation Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="body2">Rotation:</Typography>
            <IconButton onClick={handleRotateLeft} size="small">
              <RotateLeft />
            </IconButton>
            <Typography variant="body2">{rotation}°</Typography>
            <IconButton onClick={handleRotateRight} size="small">
              <RotateRight />
            </IconButton>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            Drag to reposition • Scroll or use slider to zoom • Use rotation buttons if needed
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleCrop}
          variant="contained"
          disabled={loading || !croppedAreaPixels}
          sx={{
            bgcolor: '#008080',
            '&:hover': { bgcolor: '#005252' }
          }}
        >
          {loading ? 'Processing...' : 'Crop & Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
